import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import CustomerView from '@/pages/master/customers/CustomerView';
import ProductView from '@/pages/master/products/ProductView';
import OrderView from '@/pages/sales/orders/OrderView';

import PrintLayoutView from '@/pages/production/prepress/PrintLayoutView';
import PlateDetailPage from '@/pages/tech/plates/PlateView';
import MoldDetailPage from '@/pages/tech/molds/MoldView';
import WipTransferPrintTemplate from '@/components/factory/print/WipTransferPrintTemplate';
import ProductionOrderDetailPage from '@/pages/planning/production-orders/ProductionOrderView';
import DeliveryDetailPage from '@/pages/logistics/DeliveryView';
import QuotationViewPage from '@/pages/sales/quotations/QuotationView';
import MaterialView from '@/pages/master/materials/MaterialView';
import VendorDetailPage from '@/pages/outsourcing/vendors/VendorView';
import { useQuery } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import { Loader2, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ZIndexViewType = 'customer' | 'product' | 'order' | 'design' | 'printLayout' | 'plate' | 'mold' | 'wipTransfer' | 'productionOrder' | 'delivery' | 'quotation' | 'material' | 'vendor';

function WipTransferQuickView({ transferId }: { transferId: string }) {
    const { data: transfer, isLoading } = useQuery({
        queryKey: ['wip-transfer-detail', transferId],
        queryFn: () => trpcClient.wipTransfer.getById.query({ id: transferId }),
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-12 text-sm text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin mr-2 text-indigo-600" />
                Đang tải phiếu bàn giao BTP...
            </div>
        );
    }

    if (!transfer) {
        return <div className="p-8 text-center text-sm text-slate-400">Không tìm thấy phiếu bàn giao</div>;
    }

    return (
        <div className="p-4 space-y-4">
            <WipTransferPrintTemplate transfer={transfer} />
        </div>
    );
}

interface ZIndexViewProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    type: ZIndexViewType | null;
    id: string;
    variant?: 'dialog' | 'sheet'; // default 'sheet' (full height) for large forms, 'dialog' for smaller popups
}

export function ZIndexView({ open, onOpenChange, type, id, variant = 'sheet' }: ZIndexViewProps) {
    const [isFullScreen, setIsFullScreen] = useState(false);
    const isLargeView = ['product', 'order', 'productionOrder', 'delivery', 'quotation', 'customer', 'material', 'vendor'].includes(type || '');

    React.useEffect(() => {
        if (!open) {
            setIsFullScreen(false);
        }
    }, [open]);

    if (!open || !id) return null;

    const renderContent = () => {
        switch (type) {
            case 'customer':
                return <CustomerView customerId={id} isZIndex />;
            case 'product':
                return <ProductView productId={id} isZIndex />;
            case 'material':
                return <MaterialView id={id} />;
            case 'vendor':
                return <VendorDetailPage id={id} />;
            case 'order':
                return <OrderView orderId={id} isZIndex />;
            case 'printLayout':
                return <PrintLayoutView layoutId={id} isZIndex />;
            case 'plate':
                return <PlateDetailPage plateId={id} isZIndex />;
            case 'mold':
                return <MoldDetailPage moldId={id} isZIndex />;
            case 'wipTransfer':
                return <WipTransferQuickView transferId={id} />;
            case 'productionOrder':
                return <ProductionOrderDetailPage poId={id} isZIndex />;
            case 'delivery':
                return <DeliveryDetailPage deliveryId={id} isZIndex />;
            case 'quotation':
                return <QuotationViewPage quotationId={id} isZIndex />;
            default:
                return null;
        }
    };

    if (variant === 'dialog' || variant === 'sheet') {
        // Ultra-wide container with isolated table scrolling and full-screen toggle
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent 
                    className={cn(
                        "p-0 z-[60] flex flex-col overflow-hidden transition-all duration-200",
                        isFullScreen
                            ? "!w-screen !max-w-none !h-screen !max-h-none !top-0 !left-0 !translate-x-0 !translate-y-0 rounded-none border-none shadow-none"
                            : isLargeView
                                ? "!w-[96vw] !max-w-[96vw] xl:!max-w-[1560px] 2xl:!max-w-[1720px] max-h-[92vh] h-[92vh] rounded-xl border border-slate-200/90 shadow-2xl"
                                : "max-w-4xl sm:max-w-4xl max-h-[90vh] rounded-lg border border-slate-200 shadow-xl"
                    )}
                >
                    <DialogTitle className="sr-only">Chi tiết</DialogTitle>
                    {/* Full-screen toggle button floating next to close button */}
                    {isLargeView && (
                        <div className="absolute right-12 top-2.5 z-50 flex items-center gap-1">
                            <button
                                type="button"
                                data-testid="toggle-fullscreen-btn"
                                onClick={() => setIsFullScreen(!isFullScreen)}
                                className="h-7 w-7 rounded-md flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
                                title={isFullScreen ? "Thu nhỏ giao diện" : "Phóng to toàn màn hình"}
                                aria-label={isFullScreen ? "Thu nhỏ" : "Phóng to"}
                            >
                                {isFullScreen ? (
                                    <Minimize2 className="h-4 w-4" />
                                ) : (
                                    <Maximize2 className="h-4 w-4" />
                                )}
                            </button>
                        </div>
                    )}
                    <div className="w-full h-full flex-1 flex flex-col overflow-x-hidden overflow-y-auto min-h-0">
                        {renderContent()}
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return null;
}

// ── Hook to easily use ZIndexView ──
export function useZIndexView() {
    const [open, setOpen] = useState(false);
    const [type, setType] = useState<ZIndexViewType | null>(null);
    const [id, setId] = useState<string>('');

    const openView = (t: ZIndexViewType, recordId: string) => {
        setType(t);
        setId(recordId);
        setOpen(true);
    };

    const closeView = () => {
        setOpen(false);
    };

    return {
        open,
        type,
        id,
        openView,
        closeView,
        onOpenChange: setOpen,
    };
}
